import { z } from "zod";
// Share Guard governs an exact outbound side effect. It is deliberately
// separate from the customer data-read model in data-access.ts.
export const accessLabelSchema = z.enum(["public", "internal", "restricted", "privileged"]);
export const accessProvenanceSchema = z.enum(["user", "org-internal", "external-untrusted"]);
export const accessDestinationClassSchema = z.enum([
    "org-internal", "linked-external", "unresolved-external",
]);
export const accessEgressDispositionSchema = z.enum(["allow", "warn", "approval", "block"]);
// The labels and provenance that drive the disposition are never supplied by
// the caller: the control plane derives them from the run's recorded taint
// closure, exactly as the worker path does, so a requester cannot author a
// milder evaluation than the data they touched warrants.
export const evaluateEgressInputSchema = z.object({
    sessionId: z.string().min(1), runId: z.string().min(1), toolCallId: z.string().min(1),
    toolSchemaHash: z.string().regex(/^[a-f0-9]{64}$/u), payload: z.unknown(),
    destinationClass: accessDestinationClassSchema,
    destinationSnapshot: z.record(z.string(), z.unknown()),
}).strict();
export const evaluateEgressResponseSchema = z.object({
    eventId: z.string(), disposition: accessEgressDispositionSchema,
    outcome: z.enum(["allow", "deny", "approval_required", "egress_confirmation_required"]),
    payloadHash: z.string().regex(/^[a-f0-9]{64}$/u),
}).strict();
export const accessEgressIntentResponseSchema = z.object({
    eventId: z.string().min(1), sessionId: z.string().min(1), runId: z.string().min(1),
    toolCallId: z.string().min(1), toolSchemaHash: z.string().regex(/^[a-f0-9]{64}$/u),
    requesterPrincipalId: z.string().min(1), disposition: accessEgressDispositionSchema,
    state: z.enum(["prepared", "dispatching", "confirmed", "failed", "unknown"]),
    authorizationState: z.enum(["pending", "authorized"]),
    payloadHash: z.string().regex(/^[a-f0-9]{64}$/u), payload: z.unknown(),
    destinationSnapshot: z.record(z.string(), z.unknown()),
    expiresAt: z.number().int().nonnegative(),
}).strict();
export const authorizeEgressInputSchema = z.object({
    payloadHash: z.string().regex(/^[a-f0-9]{64}$/u),
}).strict();
export const authorizeEgressResponseSchema = z.object({
    eventId: z.string().min(1), authorizationState: z.literal("authorized"),
    authorizationKind: z.enum(["confirmation", "approval"]),
    sessionId: z.string().min(1), resumeRequired: z.literal(true),
}).strict();
